import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

const TaskContext = createContext();

const initialState = {
  tasks: [],
  connections: [], // Array of { id, source, target, type }
  mindMapMode: false,
  viewMode: 'dashboard', // 'dashboard', 'ai', 'list'
  columns: [
    { id: 'todo', title: 'To Do', color: '#e3f2fd' },
    { id: 'inprogress', title: 'In Progress', color: '#fff3e0' },
    { id: 'review', title: 'Review', color: '#f3e5f5' },
    { id: 'done', title: 'Done', color: '#e8f5e8' },
  ],
  filter: 'all', // 'all', 'active', 'completed', 'high', 'medium', 'low'
  searchQuery: '',
  sortBy: 'created', // 'created', 'due', 'priority', 'title'
  sortOrder: 'desc', // 'asc', 'desc'
};

const taskReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TASK':
      return {
        ...state,
        tasks: [...state.tasks, {
          id: uuidv4(),
          title: action.payload.title,
          description: action.payload.description || '',
          priority: action.payload.priority || 'medium',
          category: action.payload.category || 'general',
          dueDate: action.payload.dueDate || null,
          completed: false,
          status: action.payload.status || 'todo',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          position: action.payload.position || { x: Math.random() * 400 + 100, y: Math.random() * 300 + 100 },
        }],
      };
    
    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id
            ? { ...task, ...action.payload.updates, updatedAt: new Date().toISOString() }
            : task
        ),
      };
    
    case 'DELETE_TASK':
      return {
        ...state,
        tasks: state.tasks.filter(task => task.id !== action.payload.id),
      };
    
    case 'TOGGLE_TASK':
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id
            ? { ...task, completed: !task.completed, updatedAt: new Date().toISOString() }
            : task
        ),
      };
    
    case 'SET_FILTER':
      return {
        ...state,
        filter: action.payload.filter,
      };
    
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload.query,
      };
    
    case 'SET_SORT':
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortOrder: action.payload.sortOrder,
      };
    
    case 'CLEAR_COMPLETED':
      return {
        ...state,
        tasks: state.tasks.filter(task => !task.completed),
      };
    
    case 'LOAD_TASKS':
      return {
        ...state,
        tasks: action.payload.tasks || [],
      };
    
    case 'TOGGLE_MIND_MAP_MODE':
      return {
        ...state,
        mindMapMode: !state.mindMapMode,
      };
    
    case 'SET_VIEW_MODE':
      return {
        ...state,
        viewMode: action.payload.mode,
      };
    
    case 'MOVE_TASK_TO_COLUMN':
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.taskId
            ? { 
                ...task, 
                status: action.payload.columnId,
                updatedAt: new Date().toISOString(),
                completed: action.payload.columnId === 'done'
              }
            : task
        ),
      };
    
    case 'REORDER_TASKS_IN_COLUMN':
      return {
        ...state,
        tasks: state.tasks.map(task => {
          const columnTasks = action.payload.tasks;
          const taskIndex = columnTasks.findIndex(t => t.id === task.id);
          if (taskIndex !== -1) {
            return { ...task, order: taskIndex };
          }
          return task;
        }),
      };
    
    case 'MOVE_TASK_TO_CATEGORY':
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.taskId
            ? { 
                ...task, 
                category: action.payload.categoryId,
                updatedAt: new Date().toISOString()
              }
            : task
        ),
      };
    
    case 'REORDER_TASKS_IN_CATEGORY':
      return {
        ...state,
        tasks: state.tasks.map(task => {
          const categoryTasks = action.payload.tasks;
          const taskIndex = categoryTasks.findIndex(t => t.id === task.id);
          if (taskIndex !== -1) {
            return { ...task, order: taskIndex };
          }
          return task;
        }),
      };
    
    case 'UPDATE_TASK_POSITION':
      return {
        ...state,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id
            ? { ...task, position: action.payload.position, updatedAt: new Date().toISOString() }
            : task
        ),
      };
    
    case 'ADD_CONNECTION':
      return {
        ...state,
        connections: [...state.connections, {
          id: uuidv4(),
          source: action.payload.source,
          target: action.payload.target,
          type: action.payload.type || 'default',
        }],
      };
    
    case 'REMOVE_CONNECTION':
      return {
        ...state,
        connections: state.connections.filter(conn => conn.id !== action.payload.id),
      };
    
    case 'UPDATE_CONNECTION':
      return {
        ...state,
        connections: state.connections.map(conn =>
          conn.id === action.payload.id
            ? { ...conn, ...action.payload.updates }
            : conn
        ),
      };
    
    default:
      return state;
  }
};

export const TaskProvider = ({ children }) => {
  const [state, dispatch] = useReducer(taskReducer, initialState);

  const addTask = (taskData) => {
    dispatch({ type: 'ADD_TASK', payload: taskData });
  };

  const updateTask = (id, updates) => {
    dispatch({ type: 'UPDATE_TASK', payload: { id, updates } });
  };

  const deleteTask = (id) => {
    dispatch({ type: 'DELETE_TASK', payload: { id } });
  };

  const toggleTask = (id) => {
    dispatch({ type: 'TOGGLE_TASK', payload: { id } });
  };

  const setViewMode = (mode) => {
    dispatch({ type: 'SET_VIEW_MODE', payload: { mode } });
  };

  const moveTaskToColumn = (taskId, columnId) => {
    dispatch({ type: 'MOVE_TASK_TO_COLUMN', payload: { taskId, columnId } });
  };

  const reorderTasksInColumn = (tasks) => {
    dispatch({ type: 'REORDER_TASKS_IN_COLUMN', payload: { tasks } });
  };

  const moveTaskToCategory = (taskId, categoryId) => {
    dispatch({ type: 'MOVE_TASK_TO_CATEGORY', payload: { taskId, categoryId } });
  };

  const reorderTasksInCategory = (tasks) => {
    dispatch({ type: 'REORDER_TASKS_IN_CATEGORY', payload: { tasks } });
  };

  const updateTaskPosition = (taskId, position) => {
    dispatch({ type: 'UPDATE_TASK_POSITION', payload: { id: taskId, position } });
  };

  const addConnection = (connection) => {
    dispatch({ type: 'ADD_CONNECTION', payload: connection });
  };

  const removeConnection = (connectionId) => {
    dispatch({ type: 'REMOVE_CONNECTION', payload: { id: connectionId } });
  };

  const setFilter = (filter) => {
    dispatch({ type: 'SET_FILTER', payload: { filter } });
  };

  const setSearchQuery = (query) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: { query } });
  };

  const setSort = (sortBy, sortOrder) => {
    dispatch({ type: 'SET_SORT', payload: { sortBy, sortOrder } });
  };

  const clearCompleted = () => {
    dispatch({ type: 'CLEAR_COMPLETED' });
  };

  const loadTasks = useCallback((tasks) => {
    dispatch({ type: 'LOAD_TASKS', payload: { tasks } });
  }, []);

  const getFilteredTasks = () => {
    let filtered = state.tasks;

    // Apply search filter
    if (state.searchQuery) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
        task.description.toLowerCase().includes(state.searchQuery.toLowerCase())
      );
    }

    // Apply status filter
    switch (state.filter) {
      case 'active':
        filtered = filtered.filter(task => !task.completed);
        break;
      case 'completed':
        filtered = filtered.filter(task => task.completed);
        break;
      case 'high':
        filtered = filtered.filter(task => task.priority === 'high');
        break;
      case 'medium':
        filtered = filtered.filter(task => task.priority === 'medium');
        break;
      case 'low':
        filtered = filtered.filter(task => task.priority === 'low');
        break;
      default:
        break;
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (state.sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'due':
          const aDate = a.dueDate ? new Date(a.dueDate) : new Date('9999-12-31');
          const bDate = b.dueDate ? new Date(b.dueDate) : new Date('9999-12-31');
          comparison = aDate - bDate;
          break;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        case 'created':
        default:
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
      }

      return state.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  };

  const getTasksByColumn = (columnId) => {
    return state.tasks
      .filter(task => task.status === columnId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  const value = {
    ...state,
    addTask,
    updateTask,
    deleteTask,
    toggleTask,
    setViewMode,
    moveTaskToColumn,
    reorderTasksInColumn,
    moveTaskToCategory,
    reorderTasksInCategory,
    updateTaskPosition,
    addConnection,
    removeConnection,
    setFilter,
    setSearchQuery,
    setSort,
    clearCompleted,
    loadTasks,
    getFilteredTasks,
    getTasksByColumn,
  };

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  );
};

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};
